import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult, DraggableLocation } from "@hello-pangea/dnd";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { OrderRequestData } from "./Domain/RemoteOrderRequests.types";
import { ServiceType } from "../../../features/Booking-Form/typings/booking-service-administration.types";
import { useRemoteFetchOrderRequestDetail } from "../../../hooks/Remote/Services/OrderRequests/useRemoteFetchOrderRequestDetail";
import { useRemoteUpdateBookingServicesByAdmin } from "../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByAdmin";
import { resolveOrderRequestBookingType } from "./functions/resolveOrderRequestBookingType.function";
import { applyServiceId } from "./functions/applyServiceId.function";
import { transformOrderRequest } from "./functions/transformOrderRequest.function";
import {
  filterDataByStatus,
  findTargetStatus,
  getOrderRequestColumnIndex,
  isLockedOrderRequestColumn,
  listBgColor,
  listTitle,
  ORDER_REQUEST_COLUMN_ARCHIVE,
  ORDER_REQUEST_COLUMN_CANCELLED,
  ORDER_REQUEST_DROPPABLE_IDS,
  ORDER_REQUEST_LIST_COLUMN_COUNT
} from "./functions/orderRequestListContents.functions";
import { DraggableCardItem } from "./DraggableCardItem.component";

type Item = OrderRequestData;

type ItemList = Item[];

// Reorder items in a single list
export const reorder = (list: Item[], startIndex: number, endIndex: number): Item[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Move item from one list to another
export const move = (
  source: Item[],
  destination: Item[],
  droppableSource: DraggableLocation,
  droppableDestination: DraggableLocation
): Record<string, Item[]> => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, removed);

  return {
    [droppableSource.droppableId]: sourceClone,
    [droppableDestination.droppableId]: destClone
  };
};

type OrderRequestListContentProps = {
  data: OrderRequestData[];
  filterByType: ServiceType[];
  setFilteredData: Dispatch<SetStateAction<OrderRequestData[]>>;
  setSelectedOrder: (o: OrderRequestData) => void;
  refetchOrderRequests: () => void;
};

export function OrderRequestListContents({
  data,
  filterByType,
  setFilteredData,
  setSelectedOrder,
  refetchOrderRequests
}: Readonly<OrderRequestListContentProps>) {
  const theme = useTheme();

  const { mutateAsync: fetchDetail } = useRemoteFetchOrderRequestDetail();
  const { mutateAsync: postOrderRequest } = useRemoteUpdateBookingServicesByAdmin();

  const [listItems, setListItems] = useState<ItemList[]>(
    Array.from({ length: ORDER_REQUEST_LIST_COLUMN_COUNT }, () => [])
  );
  const [isFolded, setIsFolded] = useState<{ [key: number]: boolean }>({
    [0]: false, // received / ORDERED
    [1]: false, // in progress / IN_PROGRESS
    [2]: false, // ready / READY
    [3]: false, // archive (past date)
    [4]: false // cancelled booking
  });

  /** update the status of the dragged item by destination's list index */
  async function handleDragEnd(targetListIndex: number, itemId: number) {
    const targetStatus = findTargetStatus(targetListIndex);
    const targetItem = data.find(item => item.id === itemId);
    if (!targetItem) {
      return;
    }

    try {
      const rawDetail = await fetchDetail(targetItem.bookingId);
      const detailData = applyServiceId(rawDetail, targetItem);
      const payload = transformOrderRequest(
        targetItem.bookingId,
        targetStatus,
        resolveOrderRequestBookingType(targetItem.placeOrZoneType),
        {
          caterings: detailData.caterings,
          equipments: detailData.equipments,
          roomLayouts: detailData.roomLayouts
        }
      );

      if (payload[0] && payload[0].servicesInfo.length === 0) {
        // Avoid wiping service lines when detail cannot be normalized.
        await refetchOrderRequests();
        return;
      }

      await postOrderRequest(payload);
      setFilteredData(previous =>
        previous.map(item =>
          item.id === itemId ? { ...item, deliveryStatus: targetStatus } : item
        )
      );
      await refetchOrderRequests();
    } catch {
      await refetchOrderRequests();
    }
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId: draggedItemId } = result;

    if (!destination) return;

    const sInd = getOrderRequestColumnIndex(source.droppableId);
    const dInd = getOrderRequestColumnIndex(destination.droppableId);
    if (sInd === -1 || dInd === -1) {
      return;
    }
    // selected/dropped item id
    const droppedItemId = Number.parseInt(draggedItemId, 10);

    if (isLockedOrderRequestColumn(sInd) || isLockedOrderRequestColumn(dInd)) {
      return;
    }

    if (sInd === dInd) {
      // dragged within the same list
      const items = reorder(listItems[sInd], source.index, destination.index);
      const newState = [...listItems];
      newState[sInd] = items;
      setListItems(newState);
      return;
    }

    // Optimistic move; refetch restores columns if the update fails.
    const moved = move(listItems[sInd], listItems[dInd], source, destination);
    const newState = [...listItems];
    newState[sInd] = moved[source.droppableId];
    newState[dInd] = moved[destination.droppableId];
    setListItems(newState);

    handleDragEnd(dInd, droppedItemId).catch(() => {
      refetchOrderRequests();
    });
  };

  const stylesByListFold = (listFolded: boolean) => {
    return {
      writingMode: listFolded ? "vertical-lr" : "initial",
      listBoxWidth: listFolded ? "auto" : 405,
      listBoxMinWidth: listFolded ? 60 : 400,
      listBoxMinHeight: listFolded ? "60vh" : "inherit",
      listBoxTitlePr: listFolded ? 1 : 0,
      listFoldingBtnTransform: listFolded ? "rotate(270deg)" : "rotate(0deg)",
      listItemDisplay: listFolded ? "none" : "inherit"
    };
  };

  const handleListFolding = (itemIndex: number) =>
    setIsFolded(prev => ({ ...prev, [itemIndex]: !prev[itemIndex] }));

  useEffect(() => {
    // group the data by status (columns: Received → In Progress → Ready → Archive → Cancelled)
    const { archiveItems, cancelledItems, receivedItems, progressItems, preparedItems } =
      filterDataByStatus(data);
    setListItems([receivedItems, progressItems, preparedItems, archiveItems, cancelledItems]);
  }, [data]);

  return (
    <Box data-testid="b-dnd-container" sx={{ display: "flex" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        {ORDER_REQUEST_DROPPABLE_IDS.map((droppableId, itemIndex) => {
          const item = listItems[itemIndex];
          const listFolded = isFolded[itemIndex];

          return (
            <Droppable
              droppableId={droppableId}
              key={droppableId}
              isDropDisabled={isLockedOrderRequestColumn(itemIndex)}
            >
              {(provided, snapshot) => (
                <Box
                  data-testid={`droppable-listBox-${itemIndex}`}
                  ref={provided.innerRef}
                  sx={[
                    {
                      background: listBgColor(snapshot.isDraggingOver, theme),
                      py: 1,
                      width: stylesByListFold(listFolded).listBoxWidth,
                      minWidth: stylesByListFold(listFolded).listBoxMinWidth,
                      minHeight: stylesByListFold(listFolded).listBoxMinHeight,
                      mr: 1
                    },
                    itemIndex === 0 ? { ml: 0 } : { ml: 1 }
                  ]}
                  {...provided.droppableProps}
                >
                  {/* title of each item list */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pl: 1,
                      pr: stylesByListFold(listFolded).listBoxTitlePr,
                      writingMode: stylesByListFold(listFolded).writingMode
                    }}
                    data-testid={`droppable-listBox-${itemIndex}-title`}
                  >
                    {/* list folding button */}
                    <IconButton
                      data-testid={`expandButton-${itemIndex}`}
                      sx={{
                        transform: stylesByListFold(listFolded).listFoldingBtnTransform,
                        transition: theme.transitions.create("transform", {
                          duration: theme.transitions.duration.shortest
                        })
                      }}
                      onClick={() => handleListFolding(itemIndex)}
                    >
                      <ExpandMore />
                    </IconButton>

                    <Typography
                      sx={{
                        ml: 0.5,
                        fontWeight: 600,
                        writingMode: stylesByListFold(listFolded).writingMode
                      }}
                    >
                      {listTitle(itemIndex, listItems)}
                    </Typography>
                  </Box>

                  {/* list of item */}
                  <Box
                    sx={{
                      height: "66vh",
                      overflow: "overlay",
                      display: stylesByListFold(listFolded).listItemDisplay
                    }}
                    data-testid={`droppable-listBox-${itemIndex}-content-parent`}
                  >
                    {item.map((item, index) => (
                      <DraggableCardItem
                        key={item.id}
                        item={item}
                        index={index}
                        itemIndex={itemIndex}
                        filterByType={filterByType}
                        setSelectedOrder={setSelectedOrder}
                        isArchive={itemIndex === ORDER_REQUEST_COLUMN_ARCHIVE}
                        isBookingCancelled={itemIndex === ORDER_REQUEST_COLUMN_CANCELLED}
                      />
                    ))}
                  </Box>
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          );
        })}
      </DragDropContext>
    </Box>
  );
}
